import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, AlertTriangle,
  Mail, Calendar, User, Building2, MapPin, FileText,
  Download, RefreshCw, Eye, Shield, Activity, History,
  ExternalLink, Send, AlertOctagon
} from 'lucide-react';
import api from '../../services/api';

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [claims, setClaims] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [resending, setResending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [viewingEvidence, setViewingEvidence] = useState(null);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);

  useEffect(() => {
    fetchRequestDetail();
    fetchAuditLogs();
  }, [id]);

  const fetchRequestDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/requests/${id}`);
      setRequest(response.data.request);
      setRecipe(response.data.recipe);
      setClaims(response.data.claims || []);
      setEvidence(response.data.evidence || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch request details:', err);
      setError('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoadingAuditLogs(true);
      const response = await api.get(`/requests/${id}/audit-logs`);
      setAuditLogs(response.data.auditLogs || []);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      // Don't show error for audit logs, just leave empty
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  const getAuditLogIcon = (action) => {
    switch (action) {
      case 'EMAIL_SENT':
        return <Send className="w-4 h-4 text-green-500" />;
      case 'EMAIL_FAILED':
        return <AlertOctagon className="w-4 h-4 text-red-500" />;
      case 'REQUEST_CREATED':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'REQUEST_RESENT':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      case 'LINK_CLICKED':
        return <ExternalLink className="w-4 h-4 text-purple-500" />;
      case 'VERIFICATION_STARTED':
        return <Activity className="w-4 h-4 text-amber-500" />;
      case 'REQUEST_APPROVED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'REQUEST_DECLINED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <History className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatAuditLogTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'gray',
      SENT: 'blue',
      IN_PROGRESS: 'amber',
      SUBMITTED: 'purple',
      NEEDS_REVIEW: 'orange',
      APPROVED: 'green',
      DECLINED: 'red',
      EXPIRED: 'gray',
    };
    return colors[status] || 'gray';
  };

  const getClaimStatusIcon = (status) => {
    switch (status) {
      case 'PASS': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'FAIL': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'PENDING': return <Clock className="w-5 h-5 text-amber-500" />;
      case 'INCONCLUSIVE': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatClaimType = (type) => {
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateProgress = () => {
    if (!recipe || !claims.length) return 0;
    const requiredClaims = recipe.required_claims || [];
    const passedClaims = claims.filter(c => c.status === 'PASS').map(c => c.claim_type);
    const matched = requiredClaims.filter(rc => passedClaims.includes(rc)).length;
    return Math.round((matched / requiredClaims.length) * 100);
  };

  const handleResendInvite = async () => {
    if (!window.confirm('Are you sure you want to resend the verification invite? This will generate a new link and invalidate the old one.')) {
      return;
    }

    try {
      setResending(true);
      await api.post(`/requests/${id}/resend`);
      alert('Verification invite resent successfully! A new email has been sent to the subject.');
      // Reload the request to get the new invite link
      fetchRequestDetail();
    } catch (err) {
      console.error('Resend invite error:', err);
      alert(err.response?.data?.error?.message || 'Failed to resend invite. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      setDownloading(true);
      const response = await api.get(`/requests/${id}/report`, {
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `verification-report-${request.subject_name}-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download report error:', err);
      alert(err.response?.data?.error?.message || 'Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleApprove = async () => {
    const notes = prompt('Add any notes for this approval (optional):');
    if (notes === null) return; // User cancelled

    if (!window.confirm('Are you sure you want to approve this verification request?')) {
      return;
    }

    try {
      setApproving(true);
      await api.post(`/requests/${id}/approve`, { notes });
      alert('Verification request approved successfully!');
      fetchRequestDetail();
    } catch (err) {
      console.error('Approve request error:', err);
      alert(err.response?.data?.error?.message || 'Failed to approve request. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleDecline = async () => {
    const reason = prompt('Please provide a reason for declining this request:');
    if (!reason) {
      alert('A reason is required to decline a request.');
      return;
    }

    const notes = prompt('Add any additional notes (optional):');
    if (notes === null) return; // User cancelled

    if (!window.confirm('Are you sure you want to decline this verification request?')) {
      return;
    }

    try {
      setDeclining(true);
      await api.post(`/requests/${id}/decline`, { reason, notes });
      alert('Verification request declined.');
      fetchRequestDetail();
    } catch (err) {
      console.error('Decline request error:', err);
      alert(err.response?.data?.error?.message || 'Failed to decline request. Please try again.');
    } finally {
      setDeclining(false);
    }
  };

  const handleViewEvidence = (evidenceItem) => {
    setViewingEvidence(evidenceItem);
  };

  const closeEvidenceModal = () => {
    setViewingEvidence(null);
  };

  const getStatusExplanation = (status) => {
    const explanations = {
      DRAFT: {
        title: 'Draft - Not Sent Yet',
        description: 'This verification request has been created but not yet sent to the subject.',
        nextAction: 'Send the verification request to the subject via email.',
        icon: <FileText className="w-5 h-5 text-gray-500" />,
        color: 'gray'
      },
      SENT: {
        title: 'Invitation Sent - Awaiting Response',
        description: 'An email invitation has been sent to the subject, but they have not yet created an account or started the verification process.',
        nextAction: 'Waiting for the subject to click the link in their email, create an account, and begin verification.',
        icon: <Mail className="w-5 h-5 text-blue-500" />,
        color: 'blue'
      },
      IN_PROGRESS: {
        title: 'In Progress - Subject is Verifying',
        description: 'The subject has started the verification process and is currently providing information and documents.',
        nextAction: 'Waiting for the subject to complete all required verification steps.',
        icon: <Clock className="w-5 h-5 text-amber-500" />,
        color: 'amber'
      },
      SUBMITTED: {
        title: 'Submitted - Ready for Review',
        description: 'The subject has completed and submitted all required information for verification.',
        nextAction: 'Review the submitted information and approve or decline the request.',
        icon: <CheckCircle className="w-5 h-5 text-purple-500" />,
        color: 'purple'
      },
      NEEDS_REVIEW: {
        title: 'Needs Review - Manual Check Required',
        description: 'Some verification checks require manual review before a decision can be made.',
        nextAction: 'Review the flagged items and make a decision.',
        icon: <AlertTriangle className="w-5 h-5 text-orange-500" />,
        color: 'orange'
      },
      APPROVED: {
        title: 'Approved - Verification Complete',
        description: 'The verification request has been reviewed and approved.',
        nextAction: 'No further action required. Verification is complete.',
        icon: <CheckCircle className="w-5 h-5 text-green-500" />,
        color: 'green'
      },
      DECLINED: {
        title: 'Declined - Verification Failed',
        description: 'The verification request has been reviewed and declined.',
        nextAction: 'No further action required unless you want to create a new request.',
        icon: <XCircle className="w-5 h-5 text-red-500" />,
        color: 'red'
      },
      EXPIRED: {
        title: 'Expired - Time Limit Exceeded',
        description: 'The verification request has expired before completion.',
        nextAction: 'Create a new verification request if still needed.',
        icon: <Clock className="w-5 h-5 text-gray-500" />,
        color: 'gray'
      }
    };
    return explanations[status] || explanations.DRAFT;
  };

  // Map step types to claim types and evidence types
  const stepClaimMap = {
    'ID_CAPTURE': ['IDENTITY_VERIFIED', 'DOCUMENT_AUTHENTIC'],
    'LIVENESS': ['LIVENESS_PASSED'],
    'ADDRESS_VERIFICATION': ['ADDRESS_VERIFIED'],
    'SANCTIONS_PEP_SCREENING': ['SANCTIONS_SCREENED', 'PEP_STATUS'],
    'ENTITY_LOOKUP': ['ENTITY_EXISTS', 'ENTITY_ACTIVE'],
    'OWNERSHIP_COLLECTION': ['DIRECTORS_CONFIRMED'],
    'UBO_VERIFICATION': ['UBO_IDENTIFIED', 'UBO_VERIFIED'],
    'ADVERSE_MEDIA_CHECK': ['ADVERSE_MEDIA_CHECKED'],
    'EDD_DECLARATION': [],
    'SOURCE_OF_FUNDS_VERIFICATION': ['SOURCE_OF_FUNDS_VERIFIED'],
    'RECURSIVE_KYB': ['OWNERSHIP_GRAPH_COMPLETE'],
    'SENIOR_REVIEW_REQUIRED': [],
  };

  const stepEvidenceMap = {
    'ID_CAPTURE': ['ID_FRONT', 'ID_BACK', 'PASSPORT_NFC'],
    'LIVENESS': ['SELFIE', 'LIVENESS_VIDEO'],
    'ADDRESS_VERIFICATION': ['POA_DOCUMENT'],
    'ENTITY_LOOKUP': ['INCORP_DOC', 'REGISTER_EXTRACT'],
    'OWNERSHIP_COLLECTION': ['OWNERSHIP_DECLARATION'],
    'UBO_VERIFICATION': ['UBO_ID'],
    'SANCTIONS_PEP_SCREENING': ['DIRECTOR_ID'],
    'SOURCE_OF_FUNDS_VERIFICATION': ['BANK_DATA_SUMMARY'],
  };

  const getStepStatus = (step) => {
    if (!claims.length) return 'pending';

    const relatedClaims = stepClaimMap[step.type] || [];
    const stepClaims = claims.filter(c => relatedClaims.includes(c.claim_type));

    if (!stepClaims.length) return 'pending';
    if (stepClaims.some(c => c.status === 'FAIL')) return 'failed';
    if (stepClaims.every(c => c.status === 'PASS')) return 'completed';
    if (stepClaims.some(c => c.status === 'PENDING')) return 'in_progress';
    return 'pending';
  };

  const getStepDetails = (step) => {
    const relatedClaims = stepClaimMap[step.type] || [];
    const relatedEvidence = stepEvidenceMap[step.type] || [];

    const stepClaims = claims.filter(c => relatedClaims.includes(c.claim_type));
    const stepEvidence = evidence.filter(e => relatedEvidence.includes(e.type));

    const completed = [];
    const pending = [];
    const missing = [];
    const failed = [];

    // Check claims
    relatedClaims.forEach(claimType => {
      const claim = stepClaims.find(c => c.claim_type === claimType);
      if (claim) {
        if (claim.status === 'PASS') {
          completed.push({ type: 'claim', name: formatClaimType(claimType), status: 'PASS' });
        } else if (claim.status === 'FAIL') {
          failed.push({ type: 'claim', name: formatClaimType(claimType), status: 'FAIL', reason: claim.reason });
        } else if (claim.status === 'PENDING') {
          pending.push({ type: 'claim', name: formatClaimType(claimType), status: 'PENDING' });
        }
      } else {
        missing.push({ type: 'claim', name: formatClaimType(claimType) });
      }
    });

    // Check evidence
    relatedEvidence.forEach(evidenceType => {
      const ev = stepEvidence.find(e => e.type === evidenceType);
      if (ev) {
        completed.push({ type: 'evidence', name: formatClaimType(evidenceType), status: 'UPLOADED' });
      } else {
        missing.push({ type: 'evidence', name: formatClaimType(evidenceType) });
      }
    });

    // Determine next action
    let nextAction = '';
    if (failed.length > 0) {
      nextAction = `Review failed verification: ${failed[0].name}`;
    } else if (missing.length > 0) {
      if (missing[0].type === 'evidence') {
        nextAction = `Upload required document: ${missing[0].name}`;
      } else {
        nextAction = `Complete verification for: ${missing[0].name}`;
      }
    } else if (pending.length > 0) {
      nextAction = `Waiting for verification: ${pending[0].name}`;
    } else if (completed.length > 0) {
      nextAction = 'Step completed';
    } else {
      nextAction = 'Not started';
    }

    return { completed, pending, missing, failed, nextAction };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Request not found'}</p>
        <button onClick={() => navigate('/requests')} className="mt-4 text-blue-600 hover:text-blue-800">
          Back to Requests
        </button>
      </div>
    );
  }

  const statusColor = getStatusColor(request.status);
  const progress = calculateProgress();
  const statusInfo = getStatusExplanation(request.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/requests')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{request.subject_name}</h1>
            <p className="text-gray-600">
              {request.type} · {request.level} · Created {formatDate(request.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`px-4 py-2 rounded-full text-sm font-medium bg-${statusColor}-100 text-${statusColor}-800`}>
            {request.status.replace('_', ' ')}
          </span>
          <button
            onClick={fetchRequestDetail}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Status Explanation */}
      <div className={`bg-${statusInfo.color}-50 border border-${statusInfo.color}-200 rounded-xl p-6`}>
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 mt-1">
            {statusInfo.icon}
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{statusInfo.title}</h2>
            <p className="text-gray-700 mb-3">{statusInfo.description}</p>
            <div className={`bg-white border border-${statusInfo.color}-300 rounded-lg p-3`}>
              <p className="text-sm font-medium text-gray-900 mb-1">Next Action:</p>
              <p className="text-sm text-gray-700">{statusInfo.nextAction}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Verification Progress</h2>
          <span className="text-2xl font-bold text-blue-600">{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          {claims.filter(c => c.status === 'PASS').length} of {recipe?.required_claims?.length || 0} required claims verified
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column (2/3) */}
        <div className="lg:col-span-2 space-y-6">

          {/* Verification Steps */}
          {recipe && recipe.steps && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-blue-600" />
                Verification Steps
              </h2>
              <div className="space-y-4">
                {recipe.steps.map((step, index) => {
                  const stepStatus = getStepStatus(step);
                  const stepDetails = getStepDetails(step);
                  const expanded = expandedSteps[index] || false;

                  return (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div
                        className="flex items-start space-x-4 p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedSteps(prev => ({ ...prev, [index]: !prev[index] }))}
                      >
                        <div className="flex-shrink-0 mt-1">
                          {stepStatus === 'completed' && <CheckCircle className="w-6 h-6 text-green-500" />}
                          {stepStatus === 'in_progress' && <Clock className="w-6 h-6 text-amber-500 animate-pulse" />}
                          {stepStatus === 'failed' && <XCircle className="w-6 h-6 text-red-500" />}
                          {stepStatus === 'pending' && <div className="w-6 h-6 rounded-full border-2 border-gray-300" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium text-gray-900">
                              Step {index + 1}: {formatClaimType(step.type)}
                            </h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              stepStatus === 'completed' ? 'bg-green-100 text-green-800' :
                              stepStatus === 'in_progress' ? 'bg-amber-100 text-amber-800' :
                              stepStatus === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {stepStatus.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {step.id}
                          </p>
                          <div className="mt-2 flex items-center text-sm">
                            <span className="font-medium text-gray-700 mr-2">Next Action:</span>
                            <span className="text-gray-600">{stepDetails.nextAction}</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'transform rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>

                      {expanded && (
                        <div className="px-4 pb-4 pt-2 bg-gray-50 border-t border-gray-200">
                          {/* Completed Items */}
                          {stepDetails.completed.length > 0 && (
                            <div className="mb-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                                Completed ({stepDetails.completed.length})
                              </h4>
                              <ul className="space-y-1">
                                {stepDetails.completed.map((item, idx) => (
                                  <li key={idx} className="text-sm text-gray-600 flex items-center">
                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                    {item.name}
                                    {item.type === 'evidence' && <span className="ml-2 text-xs text-gray-500">(Uploaded)</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Pending Items */}
                          {stepDetails.pending.length > 0 && (
                            <div className="mb-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <Clock className="w-4 h-4 mr-1 text-amber-500" />
                                In Progress ({stepDetails.pending.length})
                              </h4>
                              <ul className="space-y-1">
                                {stepDetails.pending.map((item, idx) => (
                                  <li key={idx} className="text-sm text-gray-600 flex items-center">
                                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-2 animate-pulse"></span>
                                    {item.name}
                                    <span className="ml-2 text-xs text-amber-600">(Verifying...)</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Failed Items */}
                          {stepDetails.failed.length > 0 && (
                            <div className="mb-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <XCircle className="w-4 h-4 mr-1 text-red-500" />
                                Failed ({stepDetails.failed.length})
                              </h4>
                              <ul className="space-y-1">
                                {stepDetails.failed.map((item, idx) => (
                                  <li key={idx} className="text-sm text-red-600 flex items-start">
                                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2 mt-1.5"></span>
                                    <div>
                                      <div>{item.name}</div>
                                      {item.reason && <div className="text-xs text-red-500 mt-0.5">{item.reason}</div>}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Missing Items */}
                          {stepDetails.missing.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <AlertTriangle className="w-4 h-4 mr-1 text-gray-500" />
                                Required ({stepDetails.missing.length})
                              </h4>
                              <ul className="space-y-1">
                                {stepDetails.missing.map((item, idx) => (
                                  <li key={idx} className="text-sm text-gray-600 flex items-center">
                                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                                    {item.name}
                                    {item.type === 'evidence' && <span className="ml-2 text-xs text-gray-500">(Not uploaded)</span>}
                                    {item.type === 'claim' && <span className="ml-2 text-xs text-gray-500">(Not verified)</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Claims */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-blue-600" />
              Verified Claims ({claims.length})
            </h2>
            {claims.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No claims verified yet</p>
            ) : (
              <div className="space-y-3">
                {claims.map((claim) => (
                  <div key={claim.id} className="flex items-center justify-between p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      {getClaimStatusIcon(claim.status)}
                      <div>
                        <h3 className="font-medium text-gray-900">{formatClaimType(claim.claim_type)}</h3>
                        <p className="text-sm text-gray-600">
                          {claim.method && `Method: ${claim.method}`}
                          {claim.confidence && ` · Confidence: ${(claim.confidence * 100).toFixed(0)}%`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{formatDate(claim.created_at)}</p>
                      {claim.issued_by_connector && (
                        <p className="text-xs text-gray-500">{claim.issued_by_connector}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Evidence */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Evidence Collected ({evidence.length})
            </h2>
            {evidence.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No evidence collected yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {evidence.map((item) => (
                  <div key={item.id} className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{formatClaimType(item.type)}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {item.file_name || 'Document'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(item.captured_at)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleViewEvidence(item)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View evidence"
                      >
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Right Column (1/3) */}
        <div className="space-y-6">

          {/* Request Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Request Information</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Subject</label>
                <div className="flex items-center mt-1">
                  <User className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-900">{request.subject_name}</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <div className="flex items-center mt-1">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-900">{request.subject_email}</span>
                </div>
              </div>

              {request.company_name && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Company</label>
                  <div className="flex items-center mt-1">
                    <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                    <div>
                      <div className="text-gray-900">{request.company_name}</div>
                      {request.company_number && (
                        <div className="text-xs text-gray-500">#{request.company_number}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(request.company_address_line1 || request.subject_address_line1) && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Address</label>
                  <div className="flex items-start mt-1">
                    <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                    <div className="text-sm text-gray-900">
                      {request.company_address_line1 || request.subject_address_line1}<br />
                      {(request.company_address_line2 || request.subject_address_line2) && (
                        <>{request.company_address_line2 || request.subject_address_line2}<br /></>
                      )}
                      {request.company_city || request.subject_city}, {request.company_postcode || request.subject_postcode}<br />
                      {request.company_country || request.subject_country}
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4">
                <label className="text-sm font-medium text-gray-500">Created By</label>
                <div className="mt-1 text-gray-900">
                  {request.createdBy?.first_name} {request.createdBy?.last_name}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Sent</label>
                <div className="flex items-center mt-1">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-900">{formatDate(request.sent_at)}</span>
                </div>
              </div>

              {request.expires_at && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Expires</label>
                  <div className="flex items-center mt-1">
                    <Clock className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-900">{formatDate(request.expires_at)}</span>
                  </div>
                </div>
              )}

              {request.deadline && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Deadline</label>
                  <div className="flex items-center mt-1">
                    <AlertTriangle className="w-4 h-4 text-orange-400 mr-2" />
                    <span className="text-gray-900">{formatDate(request.deadline)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Client File Status */}
          {request.ClientFile && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Client File</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      request.ClientFile.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      request.ClientFile.status === 'DECLINED' ? 'bg-red-100 text-red-800' :
                      request.ClientFile.status === 'COMPLETE' ? 'bg-blue-100 text-blue-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {request.ClientFile.status}
                    </span>
                  </div>
                </div>

                {request.ClientFile.risk_level && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Risk Level</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        request.ClientFile.risk_level === 'LOW' ? 'bg-green-100 text-green-800' :
                        request.ClientFile.risk_level === 'MEDIUM' ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {request.ClientFile.risk_level}
                      </span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-500">Completion</label>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${request.ClientFile.completion_percentage || 0}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {request.ClientFile.completion_percentage || 0}% complete
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
            <div className="space-y-3">
              <button
                onClick={handleResendInvite}
                disabled={resending}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Mail className="w-4 h-4 mr-2" />
                {resending ? 'Sending...' : 'Resend Invite'}
              </button>
              <button
                onClick={handleDownloadReport}
                disabled={downloading}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading ? 'Downloading...' : 'Download Report'}
              </button>

              {/* Approve/Decline buttons for submitted requests */}
              {['SUBMITTED', 'NEEDS_REVIEW'].includes(request.status) && (
                <>
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {approving ? 'Approving...' : 'Approve'}
                  </button>
                  <button
                    onClick={handleDecline}
                    disabled={declining}
                    className="w-full flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {declining ? 'Declining...' : 'Decline'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Recipe Details */}
          {recipe && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recipe Details</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500">Recipe ID:</span>
                  <span className="ml-2 text-gray-900 font-mono text-xs">{recipe.id}</span>
                </div>
                <div>
                  <span className="text-gray-500">Version:</span>
                  <span className="ml-2 text-gray-900">{recipe.version}</span>
                </div>
                <div>
                  <span className="text-gray-500">Region Pack:</span>
                  <span className="ml-2 text-gray-900">{recipe.region_pack}</span>
                </div>
                {recipe.ubo_threshold && (
                  <div>
                    <span className="text-gray-500">UBO Threshold:</span>
                    <span className="ml-2 text-gray-900">{recipe.ubo_threshold}%</span>
                  </div>
                )}
                {recipe.due_diligence_level && (
                  <div>
                    <span className="text-gray-500">Due Diligence:</span>
                    <span className="ml-2 text-gray-900">{recipe.due_diligence_level}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity Log Section - Full Width */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <History className="w-5 h-5 mr-2 text-blue-600" />
            Activity Log
          </h2>
          <button
            onClick={fetchAuditLogs}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loadingAuditLogs ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {loadingAuditLogs ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No activity recorded yet</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {auditLogs.map((log) => (
              <div key={log.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 mt-0.5">
                  {getAuditLogIcon(log.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{log.description}</p>
                  <div className="flex items-center mt-1 text-xs text-gray-500 space-x-3">
                    <span>{formatAuditLogTime(log.createdAt)}</span>
                    {log.user && (
                      <span className="flex items-center">
                        <User className="w-3 h-3 mr-1" />
                        {log.user.name}
                      </span>
                    )}
                    {log.externalService && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {log.externalService}
                      </span>
                    )}
                    {log.action === 'EMAIL_SENT' && (
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                        Delivered
                      </span>
                    )}
                    {log.action === 'EMAIL_FAILED' && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                        Failed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Evidence Viewer Modal */}
      {viewingEvidence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {formatClaimType(viewingEvidence.type)}
              </h2>
              <button
                onClick={closeEvidenceModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Evidence Details */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">File Name</label>
                    <p className="text-gray-900 mt-1">{viewingEvidence.file_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Type</label>
                    <p className="text-gray-900 mt-1">{formatClaimType(viewingEvidence.type)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Captured At</label>
                    <p className="text-gray-900 mt-1">{formatDate(viewingEvidence.captured_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">File Size</label>
                    <p className="text-gray-900 mt-1">
                      {viewingEvidence.file_size ? `${(viewingEvidence.file_size / 1024).toFixed(2)} KB` : 'N/A'}
                    </p>
                  </div>
                  {viewingEvidence.mime_type && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">MIME Type</label>
                      <p className="text-gray-900 mt-1">{viewingEvidence.mime_type}</p>
                    </div>
                  )}
                  {viewingEvidence.captured_by_connector && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Captured By</label>
                      <p className="text-gray-900 mt-1">{viewingEvidence.captured_by_connector}</p>
                    </div>
                  )}
                </div>

                {/* Metadata */}
                {viewingEvidence.metadata && Object.keys(viewingEvidence.metadata).length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-2 block">Metadata</label>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-xs text-gray-700 overflow-x-auto">
                        {JSON.stringify(viewingEvidence.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Extracted Data */}
                {viewingEvidence.extracted_data && Object.keys(viewingEvidence.extracted_data).length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-2 block">Extracted Data</label>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-xs text-gray-700 overflow-x-auto">
                        {JSON.stringify(viewingEvidence.extracted_data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>

              {/* File Preview/Download */}
              <div className="border-t border-gray-200 pt-6">
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    Evidence file stored securely
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Storage URI: {viewingEvidence.storage_uri || 'N/A'}
                  </p>
                  {viewingEvidence.storage_uri && (
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      onClick={() => {
                        // In production, this would download the file from secure storage
                        alert('File download functionality requires secure storage integration (S3, Azure Blob, etc.)');
                      }}
                    >
                      <Download className="w-4 h-4 inline mr-2" />
                      Download File
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeEvidenceModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestDetail;

